'use client';

import { useEffect, useRef } from 'react';
import { config } from '@/config';
import { useFetchUser } from '@/auth/hooks';
import { useExplorer } from '@/hooks/useExplorer';
import { buildPendoIdentityPayload } from '@/lib/pendo/buildPendoMetadata';
import {
  ensurePendoSnippet,
  pendoIdentify,
  pendoInitialize,
} from '@/lib/pendo/loadPendoSnippet';
import type { Config } from '@/config/schema';

/** Pendo loads only in these app environments when `pendoApiKey` is configured. */
const PENDO_APP_ENVS: ReadonlyArray<Config['appEnv']> = ['production'];

function shouldLoadPendo(): boolean {
  return !!config.pendoApiKey && PENDO_APP_ENVS.includes(config.appEnv);
}

async function waitForPendoReady(isCancelled: () => boolean): Promise<boolean> {
  for (let i = 0; i < 50; i++) {
    if (isCancelled()) {
      return false;
    }
    if (typeof window !== 'undefined' && window.pendo?.isReady?.()) {
      return true;
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  return false;
}

/**
 * Loads Pendo via CDN snippet (same pattern as chargebee-ui). Runs when `appEnv` is production
 * and `pendoApiKey` is set. Visitor/account come from Beehive session + explorer site selection when
 * logged in; anonymous otherwise. Site switches call `pendo.identify` after initial `initialize`.
 */
export function Pendo() {
  const { user, loading: authLoading = true } = useFetchUser();
  const { siteId, siteName } = useExplorer();

  const initStartedRef = useRef(false);
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldLoadPendo()) {
      return;
    }

    if (authLoading) {
      return;
    }

    const apiKey = config.pendoApiKey;
    const payload = buildPendoIdentityPayload(user, siteId, siteName);
    let cancelled = false;
    const isCancelled = () => cancelled;

    void (async () => {
      try {
        ensurePendoSnippet(apiKey);
        if (cancelled) {
          return;
        }

        const identity = {
          visitor: payload.visitor,
          ...(payload.account ? { account: payload.account } : {}),
        };

        if (!initStartedRef.current) {
          pendoInitialize(identity);
          if (cancelled) {
            return;
          }
          initStartedRef.current = true;
          lastSignatureRef.current = payload.signature;
          return;
        }

        if (lastSignatureRef.current === payload.signature) {
          return;
        }

        const ready = await waitForPendoReady(isCancelled);
        if (!ready || cancelled) {
          return;
        }

        pendoIdentify(identity);
        lastSignatureRef.current = payload.signature;
      } catch {
        // Init / identify failure is non-fatal
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, siteId, siteName]);

  return null;
}
